import { useCallback, useEffect, useMemo, useState } from 'react'
import { Tab, Tabs } from '@nextui-org/react'
import {
  Check,
  ChevronRight,
  Cloud,
  Command,
  Download,
  Keyboard,
  Loader2,
  Monitor,
  Plus,
  RefreshCw,
  Save,
  Settings2,
  Trash2,
  Wifi,
  WifiOff,
  X,
  Zap,
} from 'lucide-react'
import { reauthorizeAfterUnauthorized } from '../../../auth/oidc'
import { clearOidcReauthorization, loadSession } from '../../../auth/state'
import { useI18n } from '../../../utils/i18n'
import { fetchVibokeyDevices } from '../vibokey/api'
import {
  ACTION_TYPES,
  MEDIA_ACTIONS,
  PHYSICAL_KEYS,
  createEmptyMacro,
  loadDraft,
  normalizeBinding,
  normalizeMacro,
  normalizeStep,
  saveDraft,
  serializeKeymap,
  validateMacro,
} from '../vibokey/keymap'
import './Vibokey.css'

const AUTO_REFRESH_MS = 30_000
const mediaLabels = {
  volume_up: '音量增加',
  volume_down: '音量降低',
  play_pause: '播放 / 暂停',
  next_track: '下一首',
  previous_track: '上一首',
  mute: '静音',
}
const actionLabels = { combo: '快捷键', text: '输入文字', media: '媒体控制', macro: '宏' }

function valueOrDash(value) { return value || '—' }
function initials(value) { return String(value || 'V').slice(0, 1).toUpperCase() }
function keyLabel(key) { return key.label === '旋钮 +' ? 'CW' : key.label === '旋钮 −' ? 'CCW' : key.label === '旋钮按下' ? 'PRESS' : key.label }
function bindingSummary(binding) {
  if (binding.type === 'macro') return binding.macro.name
  if (binding.type === 'text') return binding.value || '未设置文字'
  if (binding.type === 'media') return mediaLabels[binding.action] || binding.action
  return [...binding.modifiers, binding.key].join('+')
}
function statusText(device) { return device?.status === 'online' ? '已连接' : '离线' }

function Button({ children, variant = '', className = '', ...props }) {
  return <button className={`vibokey-button ${variant ? `vibokey-button--${variant}` : ''} ${className}`.trim()} {...props}>{children}</button>
}

function StatusChip({ device }) {
  const online = device?.status === 'online'
  return <span className={`vibokey-device-chip ${online ? 'vibokey-device-chip--online' : ''}`}><span className="vibokey-device-dot" />{device ? statusText(device) : '未连接设备'}</span>
}

function Keycap({ item, binding, selected, onClick }) {
  return <button type="button" className={`vibokey-keycap ${selected ? 'vibokey-keycap--selected' : ''}`} onClick={onClick} aria-pressed={selected}>
    <span className="vibokey-keycap__label">{keyLabel(item)}</span>
    <span className="vibokey-keycap__value">{bindingSummary(binding)}</span>
  </button>
}

function KeyboardCanvas({ keymap, selectedKey, onSelect }) {
  const keys = PHYSICAL_KEYS.slice(0, 6)
  const encoders = PHYSICAL_KEYS.slice(6)
  return <div className="vibokey-keyboard" aria-label="Vibokey key layout">
    <div className="vibokey-keyboard__rows">
      {[keys.slice(0, 3), keys.slice(3, 6)].map((row) => <div className="vibokey-keyboard__row" key={row[0].id}>
        {row.map((item) => <Keycap key={item.id} item={item} binding={normalizeBinding(keymap[item.id])} selected={selectedKey === item.id} onClick={() => onSelect(item.id)} />)}
      </div>)}
    </div>
    <div className="vibokey-keyboard__encoder">
      {encoders.map((item) => <button type="button" key={item.id} className={`vibokey-encoder ${selectedKey === item.id ? 'vibokey-encoder--selected' : ''}`} onClick={() => onSelect(item.id)} aria-pressed={selectedKey === item.id}>
        <span className="vibokey-encoder__label">{keyLabel(item)}</span><span className="vibokey-encoder__hint">{item.hint}</span>
      </button>)}
    </div>
  </div>
}

function parseCombo(value) {
  const parts = value.split('+').map((part) => part.trim().toLowerCase()).filter(Boolean)
  const modifiers = parts.slice(0, -1).filter((part) => ['ctrl', 'shift', 'alt', 'meta'].includes(part))
  return { type: 'combo', modifiers, key: (parts.at(-1) || 'A').toUpperCase() }
}

function TypePicker({ value, onChange }) {
  return <div className="vibokey-type-grid">
    {ACTION_TYPES.map((item) => <button type="button" key={item.id} className={`vibokey-type ${value === item.id ? 'vibokey-type--active' : ''}`} onClick={() => onChange(item.id)}>{item.label}</button>)}
  </div>
}

function StepEditor({ step, index, onChange, onRemove }) {
  const updateType = (type) => onChange(type === 'delay' ? { op: 'delay', ms: 50 } : type === 'text' ? { op: 'text', value: '' } : { op: 'combo', modifiers: [], key: 'A' })
  const content = step.op === 'combo'
    ? <input className="vibokey-input" value={[...step.modifiers, step.key].join('+')} onChange={(event) => onChange(parseCombo(event.target.value))} placeholder="Ctrl+Shift+P" />
    : step.op === 'text'
      ? <input className="vibokey-input" value={step.value} onChange={(event) => onChange({ ...step, value: event.target.value })} placeholder="要输入的文字" />
      : <div className="vibokey-inline"><input className="vibokey-input" type="number" min="10" max="10000" step="10" value={step.ms} onChange={(event) => onChange({ ...step, ms: Number(event.target.value) })} /><span className="vibokey-muted">ms</span></div>
  return <div className="vibokey-step">
    <span className="vibokey-step__number">{String(index + 1).padStart(2, '0')}</span>
    <select className="vibokey-select vibokey-step__op" value={step.op} onChange={(event) => updateType(event.target.value)}><option value="combo">快捷键</option><option value="text">文字</option><option value="delay">延迟</option></select>
    {content}
    <button type="button" className="vibokey-step__remove" onClick={onRemove} aria-label="删除动作"><Trash2 size={15} /></button>
  </div>
}

function MacroEditor({ binding, onChange }) {
  const macro = normalizeMacro(binding.macro)
  const update = (next) => onChange({ type: 'macro', macro: normalizeMacro(next) })
  const validation = validateMacro(macro)
  return <div>
    <div className="vibokey-field"><span className="vibokey-field__label">宏名称</span><input className="vibokey-input" value={macro.name} onChange={(event) => update({ ...macro, name: event.target.value })} /></div>
    <div className="vibokey-inline" style={{ justifyContent: 'space-between', margin: '18px 0 9px' }}><span className="vibokey-field__label">动作时间线</span><span className="vibokey-count">{macro.steps.length}/32</span></div>
    <div className="vibokey-timeline">{macro.steps.map((step, index) => <StepEditor key={index} step={step} index={index} onChange={(next) => update({ ...macro, steps: macro.steps.map((item, itemIndex) => itemIndex === index ? normalizeStep(next) : item) })} onRemove={() => update({ ...macro, steps: macro.steps.filter((_, itemIndex) => itemIndex !== index) })} />)}</div>
    <Button variant="ghost" onClick={() => update({ ...macro, steps: [...macro.steps, { op: 'delay', ms: 50 }] })} disabled={macro.steps.length >= 32}><Plus size={15} />添加动作</Button>
    {!validation.valid && <p className="vibokey-note"><X size={15} />{validation.errors.join('；')}</p>}
  </div>
}

function Inspector({ item, binding, onChange }) {
  const updateType = (type) => onChange(type === 'macro' ? { type, macro: createEmptyMacro() } : type === 'text' ? { type, value: '' } : type === 'media' ? { type, action: MEDIA_ACTIONS[0] } : { type, modifiers: [], key: 'A' })
  return <div className="vibokey-panel vibokey-inspector">
    <div className="vibokey-panel__header"><div><h2 className="vibokey-panel__title">按键设置</h2><p className="vibokey-panel__hint">选择动作，变更会保存在本机草稿中。</p></div><Command size={17} color="var(--vk-cyan)" /></div>
    <div className="vibokey-panel__body">
      <div className="vibokey-inspector__selection"><div><div className="vibokey-selection-key">{item.label}</div><div className="vibokey-selection-hint">{item.hint}</div></div><span className="vibokey-count">{actionLabels[binding.type]}</span></div>
      <TypePicker value={binding.type} onChange={updateType} />
      {binding.type === 'combo' && <div className="vibokey-field"><span className="vibokey-field__label">快捷键</span><input className="vibokey-input" value={[...binding.modifiers, binding.key].join('+')} onChange={(event) => onChange(parseCombo(event.target.value))} placeholder="Ctrl+Shift+A" /></div>}
      {binding.type === 'text' && <div className="vibokey-field"><span className="vibokey-field__label">输入内容</span><textarea className="vibokey-textarea" value={binding.value} onChange={(event) => onChange({ ...binding, value: event.target.value })} placeholder="输入一段文字…" /></div>}
      {binding.type === 'media' && <div className="vibokey-field"><span className="vibokey-field__label">媒体动作</span><select className="vibokey-select" value={binding.action} onChange={(event) => onChange({ ...binding, action: event.target.value })}>{MEDIA_ACTIONS.map((action) => <option key={action} value={action}>{mediaLabels[action]}</option>)}</select></div>}
      {binding.type === 'macro' && <MacroEditor binding={binding} onChange={onChange} />}
    </div>
  </div>
}

function Workspace({ keymap, selectedKey, onSelect, onChange, onSave, onExport, dirty }) {
  const item = PHYSICAL_KEYS.find((key) => key.id === selectedKey) || PHYSICAL_KEYS[0]
  return <>
    <div className="vibokey-workspace">
      <div className="vibokey-panel vibokey-keyboard-panel"><div className="vibokey-panel__header"><div><h2 className="vibokey-panel__title">按键布局</h2><p className="vibokey-panel__hint">点击按键，在右侧编辑它的行为。</p></div><Keyboard size={17} color="var(--vk-cyan)" /></div><div className="vibokey-panel__body"><KeyboardCanvas keymap={keymap} selectedKey={selectedKey} onSelect={onSelect} /></div></div>
      <Inspector item={item} binding={normalizeBinding(keymap[selectedKey])} onChange={onChange} />
    </div>
    <SyncBar dirty={dirty} onSave={onSave} onExport={onExport} />
  </>
}

function MacroWorkspace({ keymap, selectedKey, onSelect, onChange }) {
  const macroKeys = PHYSICAL_KEYS.filter((item) => normalizeBinding(keymap[item.id]).type === 'macro')
  const item = PHYSICAL_KEYS.find((key) => key.id === selectedKey) || macroKeys[0] || PHYSICAL_KEYS[0]
  const binding = normalizeBinding(keymap[item.id])
  return <div className="vibokey-macro-layout">
    <div className="vibokey-panel"><div className="vibokey-panel__header"><div><h2 className="vibokey-panel__title">宏列表</h2><p className="vibokey-panel__hint">宏绑定在对应按键上。</p></div><Zap size={17} color="var(--vk-indigo)" /></div><div className="vibokey-panel__body"><div className="vibokey-macro-list">{(macroKeys.length ? macroKeys : PHYSICAL_KEYS).map((key) => {
      const keyBinding = normalizeBinding(keymap[key.id])
      return <button type="button" className={`vibokey-macro-item ${item.id === key.id ? 'vibokey-macro-item--active' : ''}`} key={key.id} onClick={() => onSelect(key.id)}><span><span className="vibokey-macro-item__key">{keyLabel(key)}</span><span className="vibokey-macro-item__name">{keyBinding.type === 'macro' ? keyBinding.macro.name : '未绑定宏'}</span></span><span className="vibokey-macro-item__count">{keyBinding.type === 'macro' ? `${keyBinding.macro.steps.length} 步` : '设置'}</span></button>
    })}</div></div></div>
    <div className="vibokey-panel"><div className="vibokey-panel__header"><div><h2 className="vibokey-panel__title">宏编辑器 · {item.label}</h2><p className="vibokey-panel__hint">用动作块组合快捷键、文字和延迟。</p></div><Zap size={17} color="var(--vk-indigo)" /></div><div className="vibokey-panel__body">{binding.type === 'macro' ? <MacroEditor binding={binding} onChange={onChange} /> : <div className="vibokey-empty"><div><div className="vibokey-empty__icon"><Zap size={22} /></div><h2>这个按键还不是宏</h2><p>去按键映射里把它切换为“宏”，然后就可以在这里编排动作。</p></div></div>}</div></div>
  </div>
}

function SyncBar({ dirty, onSave, onExport }) {
  return <div className="vibokey-syncbar"><div className="vibokey-syncbar__status">{dirty ? <><Monitor size={15} color="var(--vk-amber)" /><span>有未保存的本机草稿</span></> : <><Check size={15} /><span>草稿已保存到此浏览器</span></>}</div><div className="vibokey-syncbar__actions"><Button variant="ghost" onClick={onExport}><Download size={14} />导出 JSON</Button><Button variant="primary" onClick={onSave} disabled={!dirty}><Save size={14} />保存草稿</Button></div></div>
}

function DeviceCard({ device }) {
  const online = device.status === 'online'
  const info = [
    ['Agent', [device.agent.version, device.agent.platform, device.agent.architecture].filter(Boolean).join(' · ')],
    ['系统', [device.system.windowsCaption, device.system.windowsVersion, device.system.windowsBuild].filter(Boolean).join(' · ')],
    ['网络', [device.network.localIpv4, device.network.publicIp].filter(Boolean).join(' · ')],
  ]
  return <div className="vibokey-device-card"><div className="vibokey-device-card__top"><div><div className="vibokey-device-card__name">{device.name}</div><div className="vibokey-device-card__id">{device.deviceId}</div></div><StatusChip device={device} /></div><div className="vibokey-metrics"><div className="vibokey-metric"><div className="vibokey-metric__label">VID / PID</div><div className="vibokey-metric__value">{valueOrDash(device.vendorId)} / {valueOrDash(device.productId)}</div></div><div className="vibokey-metric"><div className="vibokey-metric__label">USB</div><div className="vibokey-metric__value">{device.usbConnected ? '已连接' : '未连接'}</div></div></div><div className="vibokey-info-grid">{info.map(([label, value], index) => <div className="vibokey-info" key={label}><div className="vibokey-info__title">{index === 0 ? <Monitor size={13} /> : index === 1 ? <Settings2 size={13} /> : <Wifi size={13} />}{label}</div><div className="vibokey-info__value">{valueOrDash(value)}</div></div>)}</div><div className="vibokey-panel__hint" style={{ marginTop: 14 }}>最近上报：{valueOrDash(device.lastSeenAt)} · {online ? '设备在线' : '设备目前离线'}</div></div>
}

function EmptyDevices({ onRefresh }) {
  return <div className="vibokey-empty" style={{ marginBottom: 16 }}><div><div className="vibokey-empty__icon"><WifiOff size={22} /></div><h2>未检测到 Vibokey 设备</h2><p>这不是系统故障。请让 Vibokey Agent 运行并登录同一个 Chamii 账号，然后重新读取设备。</p><div className="vibokey-empty__actions"><Button variant="primary" onClick={onRefresh}><RefreshCw size={14} />重新读取</Button></div></div></div>
}

const problemCopy = {
  forbidden: ['没有设备权限', '当前账号没有 Vibokey 设备访问权限，请联系管理员给该账号绑定设备。'],
  backend: ['后台暂时不可用', '设备服务返回了异常，配置草稿仍保存在本机，可以稍后重试。'],
  network: ['网络连接失败', '暂时无法连接设备服务，请检查网络后重试。'],
  auth: ['登录状态已失效', '请重新登录 Vibokey，再读取设备。'],
}

function ProblemState({ kind, onRetry }) {
  const [title, message] = problemCopy[kind] || problemCopy.network
  return <div className="vibokey-problem"><div><div className="vibokey-problem__icon"><WifiOff size={22} /></div><h2>{title}</h2><p>{message}</p><div className="vibokey-problem__actions"><Button variant="primary" onClick={onRetry}><RefreshCw size={14} />重试</Button></div></div></div>
}

function VibokeyConfigurator({ devices }) {
  const [tab, setTab] = useState('mapping')
  const [keymap, setKeymap] = useState(() => loadDraft())
  const [selectedKey, setSelectedKey] = useState(PHYSICAL_KEYS[0].id)
  const [dirty, setDirty] = useState(false)
  const device = devices[0]
  const updateBinding = (value) => { setKeymap((current) => ({ ...current, [selectedKey]: normalizeBinding(value) })); setDirty(true) }
  const save = () => { setKeymap(saveDraft(keymap)); setDirty(false) }
  const exportConfig = () => { const url = URL.createObjectURL(new Blob([serializeKeymap(keymap)], { type: 'application/json' })); const link = document.createElement('a'); link.href = url; link.download = 'vibokey-keymap-v1.json'; link.click(); URL.revokeObjectURL(url) }
  const tabTitle = (icon, label) => <span className="flex items-center gap-2">{icon}<span>{label}</span></span>
  return <>
    <div className="vibokey-toolbar"><Tabs className="vibokey-tabs" selectedKey={tab} onSelectionChange={(key) => setTab(String(key))} aria-label="Vibokey 工作区" variant="solid" classNames={{ tabList: 'vibokey-tab-list', cursor: 'vibokey-tab-cursor', tab: 'vibokey-tab', tabContent: 'vibokey-tab-content' }}><Tab key="mapping" title={tabTitle(<Keyboard size={14} />, '按键映射')} /><Tab key="macros" title={tabTitle(<Zap size={14} />, '宏编辑器')} /><Tab key="devices" title={tabTitle(<Wifi size={14} />, '设备状态')} /></Tabs><Button onClick={() => window.dispatchEvent(new CustomEvent('vibokey:refresh'))} disabled={false}><RefreshCw size={14} />刷新设备</Button></div>
    {!device && tab !== 'devices' && <EmptyDevices onRefresh={() => window.dispatchEvent(new CustomEvent('vibokey:refresh'))} />}
    {tab === 'mapping' && <Workspace keymap={keymap} selectedKey={selectedKey} onSelect={setSelectedKey} onChange={updateBinding} onSave={save} onExport={exportConfig} dirty={dirty} />}
    {tab === 'macros' && <MacroWorkspace keymap={keymap} selectedKey={selectedKey} onSelect={setSelectedKey} onChange={updateBinding} />}
    {tab === 'devices' && (devices.length ? <div className="vibokey-device-grid">{devices.map((item) => <DeviceCard key={item.deviceId} device={item} />)}</div> : <EmptyDevices onRefresh={() => window.dispatchEvent(new CustomEvent('vibokey:refresh'))} />)}
  </>
}

export default function Vibokey() {
  const { tx } = useI18n()
  const session = useMemo(() => loadSession(), [])
  const token = String(session?.tokens?.accessToken || '')
  const account = session?.user?.email || session?.user?.preferred_username || session?.user?.name || tx({ zh: '当前 Chamii 账号', en: 'Current Chamii account' })
  const [state, setState] = useState({ loading: true, refreshing: false, error: '', kind: '', devices: [] })
  const refresh = useCallback(async (quiet = false) => {
    setState((current) => ({ ...current, loading: quiet ? current.loading : current.devices.length === 0, refreshing: true, error: '' }))
    try {
      const devices = await fetchVibokeyDevices(token)
      clearOidcReauthorization()
      setState({ loading: false, refreshing: false, error: '', kind: '', devices })
    } catch (error) {
      const status = error?.status || 0
      if (status === 401 && reauthorizeAfterUnauthorized('/vibokey')) return
      setState((current) => ({ ...current, loading: false, refreshing: false, error: String(error?.message || error), kind: status === 403 ? 'forbidden' : status >= 500 ? 'backend' : status === 401 ? 'auth' : 'network' }))
    }
  }, [token])
  useEffect(() => {
    refresh()
    const handler = () => refresh()
    window.addEventListener('vibokey:refresh', handler)
    const timer = setInterval(() => refresh(true), AUTO_REFRESH_MS)
    return () => { window.removeEventListener('vibokey:refresh', handler); clearInterval(timer) }
  }, [refresh])
  const device = state.devices[0]
  return <div className="vibokey-app">
    <header className="vibokey-app__header"><div className="vibokey-brand"><img className="vibokey-brand__mark" src="/vibokey-icon.png" alt="Vibokey" /><div><div className="vibokey-brand__name">Vibokey</div><div className="vibokey-brand__sub">hardware workspace</div></div></div><div className="vibokey-account"><StatusChip device={device} /><Cloud size={15} /><span className="vibokey-account__email">{account}</span><span className="vibokey-avatar">{initials(account)}</span></div></header>
    <main className="vibokey-app__body"><div className="vibokey-hero"><div><div className="vibokey-eyebrow"><Keyboard size={14} />Vibokey configurator</div><h1 className="vibokey-title">把每一个按键，调成你的习惯。</h1><p className="vibokey-description">轻量的按键与宏配置工作区。配置先保存在当前浏览器，实体设备写入功能将在协议确认后开放。</p></div><StatusChip device={device} /></div>{state.loading ? <div className="vibokey-panel vibokey-loading"><Loader2 className="animate-spin" size={25} /></div> : state.error ? <ProblemState kind={state.kind} onRetry={() => refresh()} /> : <VibokeyConfigurator devices={state.devices} />}</main>
  </div>
}
